import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, Mail, Lock, Eye, EyeOff, CheckCircle, Loader2,
  Camera, AlertCircle, Upload, X, Sun, Moon, Monitor,
} from 'lucide-react';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';
const AVATAR_PHOTO_KEY = 'ivisora_avatar_photo';
const AVATAR_GRADIENT_KEY = 'ivisora_avatar_gradient';
const PREFS_KEY = 'ivisora_prefs';

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #7c3aed, #ec4899)',
  'linear-gradient(135deg, #2563eb, #38bdf8)',
  'linear-gradient(135deg, #059669, #34d399)',
  'linear-gradient(135deg, #d97706, #fbbf24)',
  'linear-gradient(135deg, #dc2626, #f87171)',
  'linear-gradient(135deg, #0891b2, #67e8f9)',
];

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE_MB = 5;

/* ─── Field input ─────────────────────────────────────────────────────────── */
const Field = ({ label, type = 'text', value, onChange, icon: Icon, placeholder, suffix, hint, error }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
      <div style={{
        display: 'flex', alignItems: 'center', height: 44,
        borderRadius: 11, border: `1.5px solid ${error ? '#fca5a5' : focused ? '#7c3aed' : '#e2e8f0'}`,
        background: error ? '#fff1f2' : focused ? '#faf5ff' : '#f8fafc',
        boxShadow: error ? '0 0 0 3px rgba(239,68,68,0.1)' : focused ? '0 0 0 3px rgba(124,58,237,0.1)' : 'none',
        paddingLeft: 12, paddingRight: suffix ? 8 : 14, gap: 9,
        transition: 'all 0.18s',
      }}>
        {Icon && <Icon className="w-4 h-4 flex-shrink-0 transition-colors" style={{ color: error ? '#f87171' : focused ? '#7c3aed' : '#94a3b8' }} />}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900"
        />
        {suffix}
      </div>
      {hint && !error && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
      {error && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
    </div>
  );
};

/* ─── Save button ─────────────────────────────────────────────────────────── */
const SaveBtn = ({ state, label = 'Save Changes', onClick, type = 'submit' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={state === 'loading'}
    className="flex items-center gap-2 h-10 px-6 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
    style={{ background: 'linear-gradient(135deg, #7c3aed, #ec4899)', boxShadow: '0 4px 14px rgba(124,58,237,0.3)' }}
  >
    {state === 'loading' ? (
      <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
    ) : state === 'success' ? (
      <><CheckCircle className="w-4 h-4" /> Saved!</>
    ) : label}
  </button>
);

/* ─── Toggle switch ───────────────────────────────────────────────────────── */
const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="relative w-11 h-6 rounded-full transition-all duration-200 flex-shrink-0"
    style={{ background: checked ? '#7c3aed' : '#e2e8f0' }}
  >
    <span
      className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-200"
      style={{ left: checked ? 'calc(100% - 22px)' : '2px' }}
    />
  </button>
);

/* ─── Theme button ────────────────────────────────────────────────────────── */
const ThemeBtn = ({ icon: Icon, label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl border transition-all text-xs font-semibold"
    style={{
      background: active ? '#f5f3ff' : '#f8fafc',
      borderColor: active ? '#7c3aed' : '#e2e8f0',
      color: active ? '#7c3aed' : '#64748b',
      boxShadow: active ? '0 0 0 2px rgba(124,58,237,0.15)' : 'none',
    }}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

/* ═══════════════════════════════════════════════════════════════
   UserSettings
═══════════════════════════════════════════════════════════════ */
const UserSettings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  /* ── Profile ── */
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [avatarGradient, setAvatarGradient] = useState(0);
  const [avatarPhoto, setAvatarPhoto] = useState(null); // base64 data URL
  const [photoPreview, setPhotoPreview] = useState(null); // local preview before save
  const [profileState, setProfileState] = useState('idle');
  const [profileErrors, setProfileErrors] = useState({});

  /* ── Security ── */
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [securityState, setSecurityState] = useState('idle');
  const [securityError, setSecurityError] = useState('');

  /* ── Preferences (single object state — no hooks in loops) ── */
  const [prefs, setPrefs] = useState({
    email_notifs: true,
    marketing: false,
    autosave: true,
  });
  const [theme, setTheme] = useState('system'); // 'light' | 'dark' | 'system'
  const [prefsState, setPrefsState] = useState('idle');

  const PREF_ITEMS = [
    { key: 'email_notifs', label: 'Email notifications', desc: 'Receive updates about your designs' },
    { key: 'marketing',    label: 'Marketing emails',    desc: 'Tips, templates, and product news' },
    { key: 'autosave',     label: 'Auto-save designs',   desc: 'Automatically save changes while editing' },
  ];

  /* Auth guard + load */
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    if (!userData) { navigate('/'); return; }
    setUser(userData);
    setName(userData.name || '');
    setEmail(userData.email || '');

    const savedGrad = localStorage.getItem(AVATAR_GRADIENT_KEY);
    if (savedGrad !== null) setAvatarGradient(parseInt(savedGrad, 10));

    const savedPhoto = localStorage.getItem(AVATAR_PHOTO_KEY);
    if (savedPhoto) setAvatarPhoto(savedPhoto);

    const savedPrefs = JSON.parse(localStorage.getItem(PREFS_KEY) || 'null');
    if (savedPrefs) {
      setPrefs(p => ({ ...p, ...savedPrefs.toggles }));
      if (savedPrefs.theme) setTheme(savedPrefs.theme);
    }
  }, [navigate]);

  /* ── Photo upload handler ── */
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast({ title: 'Invalid format', description: 'Only JPG, PNG, and WEBP are supported.', variant: 'destructive' });
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast({ title: 'File too large', description: `Max size is ${MAX_SIZE_MB}MB.`, variant: 'destructive' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    setAvatarPhoto(null);
    localStorage.removeItem(AVATAR_PHOTO_KEY);
    toast({ title: 'Photo removed', description: 'Your initials avatar is restored.' });
  };

  /* ── Profile save ── */
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!name.trim()) errs.name = 'Name is required';
    if (!email.trim() || !email.includes('@')) errs.email = 'Enter a valid email';
    if (Object.keys(errs).length) { setProfileErrors(errs); return; }
    setProfileErrors({});
    setProfileState('loading');

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (token && API_URL) {
        const res = await fetch(`${API_URL}/api/auth/profile`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: name.trim(), email: email.trim() }),
        });
        if (!res.ok) {
          const d = await res.json();
          throw new Error(d.detail || 'Failed to update profile');
        }
        const updated = await res.json();
        const newUser = { ...user, name: updated.name || name, email: updated.email || email };
        setUser(newUser);
        localStorage.setItem(USER_KEY, JSON.stringify(newUser));
      } else {
        const newUser = { ...user, name: name.trim(), email: email.trim() };
        setUser(newUser);
        localStorage.setItem(USER_KEY, JSON.stringify(newUser));
      }

      // Persist avatar gradient
      localStorage.setItem(AVATAR_GRADIENT_KEY, String(avatarGradient));

      // Persist avatar photo if a new preview was chosen
      if (photoPreview) {
        localStorage.setItem(AVATAR_PHOTO_KEY, photoPreview);
        setAvatarPhoto(photoPreview);
        setPhotoPreview(null);
      }

      setProfileState('success');
      toast({ title: 'Profile updated!', description: 'Your changes have been saved.' });
      setTimeout(() => setProfileState('idle'), 2000);
    } catch (err) {
      setProfileState('idle');
      toast({ title: 'Update failed', description: err.message, variant: 'destructive' });
    }
  };

  /* ── Password change ── */
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setSecurityError('');
    if (!currentPw) { setSecurityError('Enter your current password'); return; }
    if (newPw.length < 6) { setSecurityError('New password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { setSecurityError('New passwords do not match'); return; }
    setSecurityState('loading');
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Failed to update password');
      }
      setSecurityState('success');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      toast({ title: 'Password updated!', description: 'Your password has been changed.' });
      setTimeout(() => setSecurityState('idle'), 2000);
    } catch (err) {
      setSecurityState('idle');
      setSecurityError(err.message || 'Failed to update password');
    }
  };

  /* ── Preferences save ── */
  const handleSavePrefs = (e) => {
    e.preventDefault();
    setPrefsState('loading');
    localStorage.setItem(PREFS_KEY, JSON.stringify({ toggles: prefs, theme }));
    setTimeout(() => {
      setPrefsState('success');
      toast({ title: 'Preferences saved!', description: 'Your preferences have been updated.' });
      setTimeout(() => setPrefsState('idle'), 2000);
    }, 400);
  };

  if (!user) return null;

  const initials = user.name
    ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  /* Which avatar to show */
  const currentPhoto = photoPreview || avatarPhoto;

  const TABS = [
    { key: 'profile',     label: 'Profile',     emoji: '👤' },
    { key: 'security',    label: 'Security',     emoji: '🔒' },
    { key: 'preferences', label: 'Preferences',  emoji: '⚙️' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8">

          {/* Page header */}
          <div className="mb-8">
            <h1 className="text-2xl font-black text-gray-900 tracking-tight">Account Settings</h1>
            <p className="text-gray-500 text-sm mt-1">Manage your profile, security, and preferences</p>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            {/* ── Sidebar ── */}
            <aside className="md:w-56 flex-shrink-0">
              <nav className="bg-white rounded-2xl border border-gray-100 shadow-sm p-2 space-y-0.5 sticky top-24">
                {TABS.map(({ key, label, emoji }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 text-left"
                    style={{
                      background: activeTab === key ? 'linear-gradient(135deg, #f5f3ff, #fdf4ff)' : 'transparent',
                      color: activeTab === key ? '#7c3aed' : '#374151',
                      border: 'none', cursor: 'pointer',
                    }}
                  >
                    <span>{emoji}</span> {label}
                  </button>
                ))}
              </nav>
            </aside>

            {/* ── Content ── */}
            <div className="flex-1 min-w-0">

              {/* ══ PROFILE TAB ══ */}
              {activeTab === 'profile' && (
                <form onSubmit={handleSaveProfile} className="space-y-5">

                  {/* Avatar section */}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-5">Profile Photo</h2>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                      {/* Avatar preview */}
                      <div className="relative flex-shrink-0">
                        <div
                          className="w-24 h-24 rounded-2xl overflow-hidden flex items-center justify-center text-white text-2xl font-black shadow-lg"
                          style={{ background: currentPhoto ? 'transparent' : AVATAR_GRADIENTS[avatarGradient] }}
                        >
                          {currentPhoto ? (
                            <img src={currentPhoto} alt="avatar" className="w-full h-full object-cover" />
                          ) : initials}
                        </div>
                        {/* Camera button */}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="absolute -bottom-1.5 -right-1.5 w-8 h-8 bg-white rounded-full border-2 border-gray-200 flex items-center justify-center shadow-md hover:border-violet-400 transition-colors"
                        >
                          <Camera className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={handlePhotoChange}
                        />
                      </div>

                      {/* Upload controls */}
                      <div className="flex-1">
                        {photoPreview ? (
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                            <span className="text-xs text-amber-600 font-semibold">New photo ready — save to apply</span>
                          </div>
                        ) : null}
                        <div className="flex flex-wrap gap-2 mb-3">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:border-violet-400 hover:text-violet-600 transition-colors"
                          >
                            <Upload className="w-3.5 h-3.5" /> Upload photo
                          </button>
                          {(currentPhoto) && (
                            <button
                              type="button"
                              onClick={handleRemovePhoto}
                              className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-red-200 text-xs font-semibold text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <X className="w-3.5 h-3.5" /> Remove photo
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">JPG, PNG or WEBP. Max {MAX_SIZE_MB}MB.</p>
                      </div>
                    </div>

                    {/* Gradient picker (shown when no photo) */}
                    {!currentPhoto && (
                      <div className="mt-5 pt-5 border-t border-gray-50">
                        <p className="text-xs font-semibold text-gray-600 mb-3">Or pick an avatar color</p>
                        <div className="flex flex-wrap gap-2">
                          {AVATAR_GRADIENTS.map((grad, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setAvatarGradient(i)}
                              className="w-8 h-8 rounded-full transition-transform hover:scale-110"
                              style={{
                                background: grad,
                                outline: avatarGradient === i ? '2.5px solid #7c3aed' : '2.5px solid transparent',
                                outlineOffset: 2,
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Personal info */}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Personal Information</h2>
                    <Field
                      label="Full Name"
                      icon={User}
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Your full name"
                      error={profileErrors.name}
                    />
                    <Field
                      label="Email Address"
                      type="email"
                      icon={Mail}
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      hint="Changing your email may require re-verification."
                      error={profileErrors.email}
                    />
                    <div className="pt-2">
                      <SaveBtn state={profileState} />
                    </div>
                  </div>
                </form>
              )}

              {/* ══ SECURITY TAB ══ */}
              {activeTab === 'security' && (
                <form onSubmit={handleChangePassword} className="space-y-5">
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Change Password</h2>

                    {securityError && (
                      <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        {securityError}
                      </div>
                    )}

                    <Field
                      label="Current Password"
                      type={showCurrentPw ? 'text' : 'password'}
                      icon={Lock}
                      value={currentPw}
                      onChange={e => setCurrentPw(e.target.value)}
                      placeholder="Enter your current password"
                      suffix={
                        <button type="button" onClick={() => setShowCurrentPw(v => !v)} className="text-gray-400 hover:text-gray-600 transition-colors">
                          {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      }
                    />
                    <Field
                      label="New Password"
                      type={showNewPw ? 'text' : 'password'}
                      icon={Lock}
                      value={newPw}
                      onChange={e => setNewPw(e.target.value)}
                      placeholder="Min. 6 characters"
                      hint="Use a mix of letters, numbers, and symbols."
                      suffix={
                        <button type="button" onClick={() => setShowNewPw(v => !v)} className="text-gray-400 hover:text-gray-600 transition-colors">
                          {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      }
                    />
                    <Field
                      label="Confirm New Password"
                      type="password"
                      icon={Lock}
                      value={confirmPw}
                      onChange={e => setConfirmPw(e.target.value)}
                      placeholder="Re-enter new password"
                    />
                    <div className="pt-2">
                      <SaveBtn state={securityState} label="Update Password" />
                    </div>
                  </div>

                  {/* Danger zone */}
                  <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-6">
                    <h2 className="text-sm font-bold text-red-600 uppercase tracking-wider mb-1">Danger Zone</h2>
                    <p className="text-xs text-gray-500 mb-4">These actions are irreversible. Proceed with caution.</p>
                    <button
                      type="button"
                      className="h-9 px-4 rounded-lg border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors"
                    >
                      Delete Account
                    </button>
                  </div>
                </form>
              )}

              {/* ══ PREFERENCES TAB ══ */}
              {activeTab === 'preferences' && (
                <form onSubmit={handleSavePrefs} className="space-y-5">

                  {/* Theme */}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Theme</h2>
                    <div className="flex gap-3">
                      <ThemeBtn icon={Sun}     label="Light"  active={theme === 'light'}  onClick={() => setTheme('light')} />
                      <ThemeBtn icon={Moon}    label="Dark"   active={theme === 'dark'}   onClick={() => setTheme('dark')} />
                      <ThemeBtn icon={Monitor} label="System" active={theme === 'system'} onClick={() => setTheme('system')} />
                    </div>
                    <p className="text-xs text-gray-400 mt-3">Dark mode applies to the editor. System follows your OS setting.</p>
                  </div>

                  {/* Notification toggles */}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Notifications & Editor</h2>
                    <div className="space-y-1">
                      {PREF_ITEMS.map(({ key, label, desc }) => (
                        <div key={key} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{label}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                          </div>
                          <Toggle
                            checked={prefs[key]}
                            onChange={val => setPrefs(p => ({ ...p, [key]: val }))}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="pt-4">
                      <SaveBtn state={prefsState} label="Save Preferences" />
                    </div>
                  </div>
                </form>
              )}

            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UserSettings;
